import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react"
import { useRouter, type NextRouter } from "next/router"

import {
  transport,
  TransportRequestError,
  TransportWorkspaceScopeProvider,
  useTransportQuery,
} from "@/transport"
import { useOrganization } from "@clerk/nextjs"
import { LoadingScreen } from "@coval/ui"

import { logger } from "@/lib/logger"
import {
  buildOrganizationHomePath,
  buildWorkspacePath,
  DEFAULT_WORKSPACE_ID,
  parseLegacyOrganizationRoute,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

import {
  EMPTY_WORKSPACE_CONTEXT_VALUE,
  WorkspaceProvider,
  type WorkspaceContextValue,
} from "../contexts/WorkspaceContext"
import {
  findActiveDefaultWorkspace,
  resolveWorkspaceRoute,
} from "../utils/workspaceRouteResolution"
import { useWorkspaceFeatureFlag } from "./useWorkspaceFeatureFlag"

interface WorkspaceProviderRouteProps {
  children: ReactNode
}

type LegacyOrganizationRoute = NonNullable<
  ReturnType<typeof parseLegacyOrganizationRoute>
>
type WorkspaceRoute = NonNullable<ReturnType<typeof parseWorkspaceRoute>>

const INVARIANT_MISSING_ACTIVE_DEFAULT =
  "WorkspaceProvider invariant: listWorkspaces response must include an active DEFAULT workspace"
const log = logger("WorkspaceProviderRoute")

function WorkspaceAccessErrorState({
  asPath,
  detail,
  onChooseOrganization,
  onRetry,
  organizationSlug,
  retryLabel = "Retry this page",
  title,
}: {
  asPath: string
  detail: string
  onChooseOrganization: () => void
  onRetry: () => void
  organizationSlug: string | null
  retryLabel?: string
  title: string
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-default px-6">
      <div className="w-full max-w-lg rounded-lg border border-coval-border bg-surface-0 p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-coval-text">{title}</h1>
        <p className="mt-2 text-sm text-coval-text-muted">
          {detail} You can retry this page or choose another organization.
        </p>
        <dl className="mt-4 rounded-md bg-surface-1 p-3 text-left text-xs text-coval-text-muted">
          <div className="flex gap-2">
            <dt className="shrink-0 font-medium text-coval-text">Org:</dt>
            <dd className="truncate">{organizationSlug ?? "Unknown"}</dd>
          </div>
          <div className="mt-1 flex gap-2">
            <dt className="shrink-0 font-medium text-coval-text">Route:</dt>
            <dd className="truncate">{asPath}</dd>
          </div>
        </dl>
        <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-md border border-coval-border bg-surface-0 px-4 text-sm font-medium text-coval-text transition-colors hover:bg-surface-0-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coval-info-border focus-visible:ring-offset-2"
            onClick={onRetry}
          >
            {retryLabel}
          </button>
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-md border border-coval-border bg-surface-0 px-4 text-sm font-medium text-coval-text transition-colors hover:bg-surface-0-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coval-info-border focus-visible:ring-offset-2"
            onClick={onChooseOrganization}
          >
            Choose another organization
          </button>
        </div>
      </div>
    </div>
  )
}

function isRecoverableWorkspaceAccessError(error: unknown): boolean {
  return (
    error instanceof TransportRequestError &&
    (error.status === 401 || error.status === 403)
  )
}

export function WorkspaceProviderRoute({
  children,
}: WorkspaceProviderRouteProps) {
  const router = useRouter()
  const workspaceRoute = useMemo(
    () => parseWorkspaceRoute(router.asPath),
    [router.asPath],
  )
  const legacyRoute = useMemo(
    () => parseLegacyOrganizationRoute(router.asPath),
    [router.asPath],
  )
  const organizationRoute = workspaceRoute ?? legacyRoute

  if (!organizationRoute) {
    return (
      <WorkspaceProvider value={EMPTY_WORKSPACE_CONTEXT_VALUE}>
        {children}
      </WorkspaceProvider>
    )
  }

  return (
    <OrganizationWorkspaceProviderRoute
      legacyOrWorkspaceRoute={organizationRoute}
      router={router}
      workspaceRoute={workspaceRoute}
    >
      {children}
    </OrganizationWorkspaceProviderRoute>
  )
}

function OrganizationWorkspaceProviderRoute({
  children,
  legacyOrWorkspaceRoute,
  router,
  workspaceRoute,
}: WorkspaceProviderRouteProps & {
  legacyOrWorkspaceRoute: LegacyOrganizationRoute | WorkspaceRoute
  router: NextRouter
  workspaceRoute: WorkspaceRoute | null
}) {
  const [lastActiveContextValue, setLastActiveContextValue] =
    useState<WorkspaceContextValue | null>(null)
  const {
    organization: activeOrganization,
    isLoaded: isActiveOrganizationLoaded,
  } = useOrganization()
  const activeOrganizationSlug = activeOrganization?.slug ?? null
  const isRouteOrganizationActive =
    !isActiveOrganizationLoaded ||
    activeOrganizationSlug === null ||
    legacyOrWorkspaceRoute.organizationSlug === activeOrganizationSlug
  const { isWaitingForWorkspaceFlag, isWorkspacesEnabled, organizationId } =
    useWorkspaceFeatureFlag()
  const shouldFetchWorkspaces = isWorkspacesEnabled && isRouteOrganizationActive
  // Settling a sentinel URL must not depend on the workspaces flag: `proxy.ts`
  // upgrades organization URLs to the `default` segment without knowing the
  // flag, so a sentinel URL arrives for flag-disabled organizations too, and
  // the workspace list is the only thing that can turn it into a real id. The
  // fetch itself is therefore not flag-gated, but everything that changes what
  // the page renders (error screens, loading gates, the unavailable state) is,
  // so a flag-disabled organization keeps rendering unscoped and the settle
  // stays a silent URL rewrite.
  const isDefaultSentinelRoute =
    workspaceRoute !== null &&
    workspaceRoute.workspaceId === DEFAULT_WORKSPACE_ID &&
    isRouteOrganizationActive
  const workspacesQuery = useTransportQuery({
    operation: transport.operations.listWorkspaces,
    options: {
      enabled: shouldFetchWorkspaces || isDefaultSentinelRoute,
      // A flag-disabled organization runs this query only to settle the URL,
      // and the UI deliberately ignores the outcome, so keep those failures
      // out of error reporting.
      reportErrors: shouldFetchWorkspaces,
    },
  })
  const resolved = useMemo(
    () =>
      resolveWorkspaceRoute({
        asPath: router.asPath,
        isWorkspacesEnabled: isWorkspacesEnabled && isRouteOrganizationActive,
        organizationRoute: legacyOrWorkspaceRoute,
        workspaceResources: workspacesQuery.data?.workspaces ?? [],
        workspaceRoute,
      }),
    [
      isRouteOrganizationActive,
      isWorkspacesEnabled,
      legacyOrWorkspaceRoute,
      router.asPath,
      workspaceRoute,
      workspacesQuery.data,
    ],
  )
  const missingActiveDefaultWorkspace =
    shouldFetchWorkspaces &&
    Boolean(workspacesQuery.data) &&
    !resolved.hasActiveDefaultWorkspace
  const recoverableWorkspaceAccessError =
    shouldFetchWorkspaces &&
    isRecoverableWorkspaceAccessError(workspacesQuery.error)
  const requestedWorkspaceUnavailable =
    shouldFetchWorkspaces &&
    Boolean(workspacesQuery.data) &&
    workspaceRoute !== null &&
    !resolved.requestedWorkspaceAvailable
  // The URL must always settle on a real workspace id. The sentinel segment is
  // a transient placeholder the proxy and legacy redirects use, so once the
  // workspace list proves which workspace is the active default, move the URL
  // to that workspace's id form. The lookup reads the list directly rather
  // than the resolved context, because the context is deliberately empty while
  // the workspaces flag is off or unresolved and the settle must still run
  // then.
  const sentinelRedirectWorkspaceId = isDefaultSentinelRoute
    ? (findActiveDefaultWorkspace(workspacesQuery.data?.workspaces ?? [])?.id ??
      null)
    : null

  useEffect(() => {
    if (sentinelRedirectWorkspaceId === null || workspaceRoute === null) {
      return
    }
    const url = new URL(router.asPath, "https://coval.local")
    url.pathname = buildWorkspacePath(
      legacyOrWorkspaceRoute.organizationSlug,
      sentinelRedirectWorkspaceId,
      workspaceRoute.relativePath,
    )
    void router.replace(url.pathname + url.search + url.hash)
  }, [
    legacyOrWorkspaceRoute,
    router,
    router.asPath,
    sentinelRedirectWorkspaceId,
    workspaceRoute,
  ])

  if (isRouteOrganizationActive) {
    if (workspaceRoute && isWaitingForWorkspaceFlag) {
      return <LoadingScreen />
    }

    if (recoverableWorkspaceAccessError) {
      return (
        <WorkspaceAccessErrorState
          asPath={router.asPath}
          detail="The workspace request was rejected for this organization. This can happen if your current session is not authorized for this organization."
          organizationSlug={legacyOrWorkspaceRoute.organizationSlug}
          title="We could not access this organization workspace"
          onChooseOrganization={() => {
            void router.push("/select-org")
          }}
          onRetry={() => {
            router.reload()
          }}
        />
      )
    }

    if (shouldFetchWorkspaces && workspacesQuery.error) {
      throw workspacesQuery.error
    }

    if (shouldFetchWorkspaces && !workspacesQuery.data) {
      return <LoadingScreen />
    }

    // With the flag off the context is the constant unscoped value, so the
    // settle is a pure URL rewrite and the children stay mounted through it.
    // Only a flag-on organization needs the loader while its URL settles.
    if (shouldFetchWorkspaces && sentinelRedirectWorkspaceId !== null) {
      return <LoadingScreen />
    }

    if (missingActiveDefaultWorkspace) {
      log.warn(INVARIANT_MISSING_ACTIVE_DEFAULT, {
        asPath: router.asPath,
        organizationId,
        organizationSlug: legacyOrWorkspaceRoute.organizationSlug,
        workspaceSummary: workspacesQuery.data?.workspaces.map((workspace) => ({
          id: workspace.id,
          status: workspace.status,
          workspace_type: workspace.workspace_type,
        })),
      })
      throw new Error(INVARIANT_MISSING_ACTIVE_DEFAULT)
    }

    if (requestedWorkspaceUnavailable) {
      return (
        <WorkspaceAccessErrorState
          asPath={router.asPath}
          detail="The linked workspace is missing, archived, deleted, or belongs to another organization."
          organizationSlug={legacyOrWorkspaceRoute.organizationSlug}
          retryLabel="Go to Default workspace"
          title="This workspace is unavailable"
          onChooseOrganization={() => {
            void router.push("/select-org")
          }}
          onRetry={() => {
            const defaultWorkspaceId =
              resolved.contextValue.currentWorkspace?.id
            void router.push(
              defaultWorkspaceId
                ? buildWorkspacePath(
                    legacyOrWorkspaceRoute.organizationSlug,
                    defaultWorkspaceId,
                  )
                : buildOrganizationHomePath(
                    legacyOrWorkspaceRoute.organizationSlug,
                  ),
            )
          }}
        />
      )
    }
  }

  if (
    isRouteOrganizationActive &&
    lastActiveContextValue !== resolved.contextValue
  ) {
    setLastActiveContextValue(resolved.contextValue)
  }
  const contextValue = isRouteOrganizationActive
    ? resolved.contextValue
    : (lastActiveContextValue ?? resolved.contextValue)

  return (
    <TransportWorkspaceScopeProvider
      workspaceId={contextValue.currentWorkspace?.id ?? null}
    >
      <WorkspaceProvider value={contextValue}>
        <Fragment key={contextValue.currentWorkspace?.id ?? "global"}>
          {children}
        </Fragment>
      </WorkspaceProvider>
    </TransportWorkspaceScopeProvider>
  )
}
